/* eslint-disable react/prop-types */
/* eslint-disable react-native/no-inline-styles */
/* eslint-disable react-native/no-color-literals */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  View,
  StyleSheet,
  Dimensions,
  Linking,
  ScrollView,
  TouchableOpacity,
  Modal,
  TouchableWithoutFeedback,
} from 'react-native';

import { Loader } from '@components/screen/Layout';
import SociollaCard from '@components/sociolla/SociollaCard';
import SociollaText from '@components/sociolla/SociollaText';
import CardHeaderCustom from '@/screens/home/components/snippets/CardHeaderCustom';
import WebViews from '@/plugins/webview';
import Input from '@components/form/Input';
import Image from '@components/global/Image';

import { isWeb, isNative, isMobileWeb } from '@global/screens';
import Colors from '@global/colors';
import {
  getGoogleMapKey,
  getMapWebViewBaseUrl,
  isVn,
  defaultCountry,
} from '@global/helpers';
import {
  getCustomers,
  getAllStates,
  googleMapsAutocomplete,
  googleMapsPlaceDetail,
} from '@api/ms-account';
import { MsB2b } from '@api/ms-b2b';
import useApi from '@/hooks/useApi';
import Toast from 'react-native-toast-message';
import SelectBox from '@components/form/SelectBox';
import { useSelector, useDispatch } from 'react-redux';
import * as Resources from '@/store/resources/actions';

const dimension = Dimensions.get('window');

const INDONESIA_CENTER = { lat: -2.5489, lng: 118.0149 };
const VIETNAM_CENTER = { lat: 14.0583, lng: 108.2772 };

const DEFAULT_ZOOM_ID = isWeb ? 6 : 3;
const DEFAULT_ZOOM_VN = isWeb ? 7 : 4;

function generateMapScript(
  customers,
  center,
  zoom,
  preserveViewport = false,
  savedBounds = null,
  savedCenter = null,
  savedZoom = null,
  googleMapKey,
) {
  const initialMarkers = customers.flatMap((customer) => {
    const customerId = customer._id || '';
    const name = customer.name || '';
    const deliveryAddresses = customer.delivery_address || [];

    return deliveryAddresses
      .map((deliveryAddress, index) => {
        if (
          !deliveryAddress?.lat_long?.latitude ||
          !deliveryAddress?.lat_long?.longitude
        ) {
          return null;
        }

        const lat = deliveryAddress.lat_long.latitude;
        const lng = deliveryAddress.lat_long.longitude;
        const addressName = deliveryAddress.name || '';

        const addressParts = [];
        if (deliveryAddress.street) {
          addressParts.push(deliveryAddress.street);
        }
        if (deliveryAddress.street2) {
          addressParts.push(deliveryAddress.street2);
        }
        if (deliveryAddress.district?.name) {
          addressParts.push(deliveryAddress.district.name);
        }
        if (deliveryAddress.city?.name) {
          addressParts.push(deliveryAddress.city.name);
        }
        if (deliveryAddress.province?.name) {
          addressParts.push(deliveryAddress.province.name);
        }
        if (deliveryAddress.state?.name) {
          addressParts.push(deliveryAddress.state.name);
        }
        if (deliveryAddress.postal_code) {
          addressParts.push(deliveryAddress.postal_code);
        }
        if (deliveryAddress.country?.name) {
          addressParts.push(deliveryAddress.country.name);
        }
        const fullAddress =
          addressParts.length > 0 ? addressParts.join(', ') : '';

        return {
          id: `${String(customerId)}_${index}`,
          lat,
          lng,
          name: name.replace(/'/g, "\\'"),
          addressName: addressName.replace(/'/g, "\\'"),
          fullAddress: fullAddress.replace(/'/g, "\\'"),
        };
      })
      .filter(Boolean);
  });

  const mapCenter = preserveViewport && savedCenter ? savedCenter : center;
  const mapZoom = preserveViewport && savedZoom ? savedZoom : zoom;

  const safeCenterLat = mapCenter?.lat || center?.lat || -2.5489;
  const safeCenterLng = mapCenter?.lng || center?.lng || 118.0149;
  const safeZoom = mapZoom || zoom || 5;

  const savedCenterLat = savedCenter?.lat;
  const savedCenterLng = savedCenter?.lng;
  const savedZoomValue = savedZoom;

  const baseUrl = getMapWebViewBaseUrl();

  return `
    <!DOCTYPE html>
    <html>
    <head>
      <title>Customer Pinpoint Dashboard</title>
      <meta charset="utf-8" />
      <meta name="viewport" content="width=device-width, initial-scale=1.0">
      <meta name="referrer" content="no-referrer-when-downgrade" />
      <base href="${baseUrl}/" />
      <style>
        body { margin: 0; padding: 0; }
        #map { width: 100%; height: 100vh; }
      </style>
    </head>
    <body>
      <div id="map"></div>
      <script>
        var map;
        var markers = [];
        var infoWindow;
        var lastBounds = null;
        var lastZoom = ${
          preserveViewport && savedZoomValue !== undefined
            ? savedZoomValue
            : safeZoom
        };
        var loading = false;
        var isRestoringViewport = ${
          preserveViewport && savedBounds ? 'true' : 'false'
        };
        var debounceTimer = null;
        var mapInitialized = false;
        var userHasInteracted = false;
        
        function initializeMap() {
          if (mapInitialized) {
            return;
          }
          mapInitialized = true;
          
          try {
          var center = ${
            preserveViewport &&
            savedCenterLat !== undefined &&
            savedCenterLng !== undefined &&
            savedZoomValue !== undefined
              ? `{ lat: ${savedCenterLat}, lng: ${savedCenterLng} }`
              : `{ lat: ${safeCenterLat}, lng: ${safeCenterLng} }`
          };
          var zoom = ${
            preserveViewport && savedZoomValue !== undefined
              ? savedZoomValue
              : safeZoom
          };
          
          map = new google.maps.Map(document.getElementById('map'), {
            center: center,
            zoom: zoom,
            zoomControl: true,
            mapTypeControl: false,
            streetViewControl: false,
            fullscreenControl: false,
            gestureHandling: 'auto'
          });
          
          infoWindow = new google.maps.InfoWindow();
          
          ${
            preserveViewport &&
            savedBounds &&
            savedCenterLat !== undefined &&
            savedCenterLng !== undefined &&
            savedZoomValue !== undefined
              ? `
          google.maps.event.addListenerOnce(map, 'idle', function() {
            if (!userHasInteracted) {
              isRestoringViewport = true;
              map.setCenter({ lat: ${savedCenterLat}, lng: ${savedCenterLng} });
              map.setZoom(${savedZoomValue});
              
              setTimeout(function() {
                lastBounds = map.getBounds();
                lastZoom = map.getZoom();
                isRestoringViewport = false;
              }, 300);
            } else {
              lastBounds = map.getBounds();
              lastZoom = map.getZoom();
            }
          });
          `
              : ''
          }
          
          var initialMarkersData = ${JSON.stringify(initialMarkers)};
          if (initialMarkersData && initialMarkersData.length > 0) {
            addMarkers(initialMarkersData);
          }
          
          var isMapMoving = false;
          var idleDebounceTimer = null;
          
          google.maps.event.addListener(map, 'dragstart', function() {
            userHasInteracted = true;
            isMapMoving = true;
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
            }
          });
          
          google.maps.event.addListener(map, 'zoom_changed', function() {
            if (!isRestoringViewport) {
              userHasInteracted = true;
              isMapMoving = true;
            }
            if (debounceTimer) {
              clearTimeout(debounceTimer);
              debounceTimer = null;
            }
          });
          
          google.maps.event.addListener(map, 'idle', function() {
            if (isRestoringViewport || loading) {
              return;
            }
            
            if (idleDebounceTimer) {
              clearTimeout(idleDebounceTimer);
            }
            
            idleDebounceTimer = setTimeout(function() {
              if (loading || isRestoringViewport) {
                return;
              }
              notifyBoundsChange();
              isMapMoving = false;
            }, 500);
          });
          
          google.maps.event.addListenerOnce(map, 'idle', function() {
            setTimeout(function() {
              lastBounds = map.getBounds();
              lastZoom = map.getZoom();
              if (!isRestoringViewport) {
                notifyBoundsChange();
              }
            }, 500);
          });
          
          notifyMapLoaded();
          } catch (error) {
            notifyMapError('Google Maps initialization error: ' + error.message);
          }
        }
        
        function notifyMapError(errorMessage) {
          var payload = JSON.stringify({
            type: 'map_error',
            error: errorMessage || 'Google Maps failed to load'
          });
          
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(payload);
          } else if (window.parent && window.parent !== window) {
            window.parent.postMessage(payload, '*');
          } else if (window.postMessage) {
            window.postMessage(payload, '*');
          }
        }
        
        function notifyMapLoaded() {
          var payload = JSON.stringify({
            type: 'map_loaded'
          });
          
          if (window.ReactNativeWebView) {
            window.ReactNativeWebView.postMessage(payload);
          } else if (window.parent && window.parent !== window) {
            window.parent.postMessage(payload, '*');
          } else if (window.postMessage) {
            window.postMessage(payload, '*');
          }
        }
        
        window.gm_authFailure = function() {
          notifyMapError('Google Maps authentication failed');
        };
        
        var mapLoadTimeout = setTimeout(function() {
          if (!map) {
            notifyMapError('Google Maps failed to load - timeout');
          }
        }, 15000);
        
        window.initMap = function() {
          clearTimeout(mapLoadTimeout);
          initializeMap();
        };
        
        window.addEventListener('error', function(event) {
          if (event.message && (event.message.includes('google') || event.message.includes('maps'))) {
            clearTimeout(mapLoadTimeout);
            notifyMapError('Google Maps script error');
          }
        }, true);
        
        function addMarkers(markersData) {
          if (!markersData || !Array.isArray(markersData) || markersData.length === 0) {
            return;
          }
          
          markersData.forEach(function(markerData) {
            if (!markerData || markerData.lat === undefined || markerData.lat === null || 
                markerData.lng === undefined || markerData.lng === null) {
              return;
            }
            
            var marker = new google.maps.Marker({
              position: { lat: markerData.lat, lng: markerData.lng },
              map: map,
              title: markerData.name || 'Unknown Customer'
            });
            
            var contentString = '<div style="padding: 10px; min-width: 200px;">' +
              '<div style="margin-bottom: 8px;"><b style="font-size: 14px;">' + 
              (markerData.name || 'Unknown Customer').replace(/'/g, "\\'") + 
              '</b></div>';
            
            if (markerData.fullAddress) {
              contentString += '<div style="margin-bottom: 8px; color: #666; font-size: 12px; line-height: 1.4;">' + 
                (markerData.fullAddress || '').replace(/'/g, "\\'") + 
                '</div>';
            } else if (markerData.addressName) {
              contentString += '<div style="margin-bottom: 8px; color: #666; font-size: 12px;">' + 
                (markerData.addressName || '').replace(/'/g, "\\'") + 
                '</div>';
            }
            
            contentString += '<div style="margin-top: 10px;">' +
              '<a href="#" id="open-gmaps-' + markerData.id + '" style="color: #1976d2; text-decoration: none; font-size: 12px; font-weight: 500;">' +
              'Click here to open in Google Maps' +
              '</a>' +
              '</div>' +
              '</div>';
            
            marker.addListener('click', function() {
              infoWindow.setContent(contentString);
              infoWindow.open(map, marker);
              
              setTimeout(function() {
                var linkElement = document.getElementById('open-gmaps-' + markerData.id);
                if (linkElement) {
                  linkElement.addEventListener('click', function(e) {
                    e.preventDefault();
                    var payload = JSON.stringify({
                      type: 'open_google_maps',
                      lat: markerData.lat,
                      lng: markerData.lng,
                      customerId: markerData.id || 'unknown',
                      customerName: markerData.name || 'Unknown Customer',
                      fullAddress: markerData.fullAddress || ''
                    });
                    
                    if (window.ReactNativeWebView) {
                      window.ReactNativeWebView.postMessage(payload);
                    } else if (window.parent && window.parent !== window) {
                      window.parent.postMessage(payload, '*');
                    } else if (window.postMessage) {
                      window.postMessage(payload, '*');
                    }
                  });
                }
              }, 100);
            });
            
            markers.push(marker);
          });
        }
        
        function notifyBoundsChange() {
          if (loading || isRestoringViewport) {
            return;
          }
          
          var currentBounds = map.getBounds();
          var currentZoom = map.getZoom();
          
          if (!currentBounds) {
            return;
          }
          
          var ne = currentBounds.getNorthEast();
          var sw = currentBounds.getSouthWest();
          
          var boundsChanged = !lastBounds || 
            Math.abs(ne.lat() - lastBounds.getNorthEast().lat()) > 0.05 ||
            Math.abs(ne.lng() - lastBounds.getNorthEast().lng()) > 0.05 ||
            Math.abs(sw.lat() - lastBounds.getSouthWest().lat()) > 0.05 ||
            Math.abs(sw.lng() - lastBounds.getSouthWest().lng()) > 0.05;
          var zoomChanged = currentZoom !== lastZoom;
          
          if (boundsChanged || zoomChanged) {
            var payload = {
              type: 'bounds_change',
              bounds: {
                north: ne.lat(),
                south: sw.lat(),
                east: ne.lng(),
                west: sw.lng()
              },
              zoom: currentZoom,
              center: {
                lat: map.getCenter().lat(),
                lng: map.getCenter().lng()
              }
            };
            
            if (window.ReactNativeWebView) {
              window.ReactNativeWebView.postMessage(JSON.stringify(payload));
            } else if (window.parent && window.parent !== window) {
              window.parent.postMessage(JSON.stringify(payload), '*');
            } else if (window.postMessage) {
              window.postMessage(JSON.stringify(payload), '*');
            }
            
            lastBounds = currentBounds;
            lastZoom = currentZoom;
          }
        }
        
        function debouncedNotifyBoundsChange() {
          if (debounceTimer) {
            clearTimeout(debounceTimer);
          }
          debounceTimer = setTimeout(function() {
            notifyBoundsChange();
          }, 1000);
        }
        
        function setMapInteractionEnabled(enabled) {
          if (map) {
            map.setOptions({
              draggable: enabled,
              zoomControl: enabled,
              scrollwheel: enabled,
              disableDoubleClickZoom: !enabled
            });
          }
        }
        
        function updateMarkers(newMarkersData) {
          loading = true;
          setMapInteractionEnabled(false);
          
          try {
            markers.forEach(function(marker) {
              marker.setMap(null);
            });
            markers = [];
            
            if (infoWindow) {
              infoWindow.close();
            }
            
            if (!newMarkersData || !Array.isArray(newMarkersData) || newMarkersData.length === 0) {
              loading = false;
              setMapInteractionEnabled(true);
              return;
            }
            
            addMarkers(newMarkersData);
            
            loading = false;
            setMapInteractionEnabled(true);
          } catch (e) {
            console.error('Error updating markers:', e);
            loading = false;
            setMapInteractionEnabled(true);
          }
        }
        
        function focusMapToLocation(center, zoom, addresses, viewport) {
          if (!map) return;
          try {
            if (viewport && viewport.northeast && viewport.southwest) {
              var bounds = new google.maps.LatLngBounds(
                new google.maps.LatLng(viewport.southwest.lat, viewport.southwest.lng),
                new google.maps.LatLng(viewport.northeast.lat, viewport.northeast.lng)
              );
              map.fitBounds(bounds, { padding: 5 });
              setTimeout(function() {
                if (map) {
                  var currentZoom = map.getZoom();
                  if (currentZoom < 14) {
                    map.setZoom(Math.min(currentZoom + 1, 14));
                  }
                }
              }, 500);
            } else if (addresses && addresses.length > 1) {
              setTimeout(function() {
                var bounds = new google.maps.LatLngBounds();
                addresses.forEach(function(addr) {
                  bounds.extend(new google.maps.LatLng(addr.lat, addr.lng));
                });
                map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
              }, 100);
            } else {
              map.panTo(center);
              map.setZoom(zoom);
            }
          } catch (e) {
            console.error('Error focusing map:', e);
          }
        }
        
        window.focusMapToLocation = focusMapToLocation;
        
        window.addEventListener('message', function(event) {
          try {
            var data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            if (data && data.type === 'update_markers' && data.markers) {
              updateMarkers(data.markers);
            } else if (data && data.type === 'focus_map') {
              focusMapToLocation(data.center, data.zoom, data.addresses, data.viewport);
            }
          } catch (e) {
            console.error('Error handling message:', e);
          }
        });
        
        document.addEventListener('message', function(event) {
          try {
            var data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data;
            if (data && data.type === 'update_markers' && data.markers) {
              updateMarkers(data.markers);
            } else if (data && data.type === 'focus_map') {
              focusMapToLocation(data.center, data.zoom, data.addresses, data.viewport);
            }
          } catch (e) {
            console.error('Error handling document message:', e);
          }
        });
        
        window.updateMapMarkers = updateMarkers;
        window.setMapInteractionEnabled = setMapInteractionEnabled;
      </script>
      <script>
        var script = document.createElement('script');
        script.src = 'https://maps.googleapis.com/maps/api/js?key=${googleMapKey}&callback=initMap&v=weekly';
        script.async = true;
        script.defer = true;
        script.onerror = function() {
          if (window.gm_authFailure) {
            window.gm_authFailure();
          }
        };
        document.head.appendChild(script);
      </script>
    </body>
    </html>
  `;
}

const CustomerPinpointDashboard = () => {
  const webViewRef = useRef(null);
  const mapRef = useRef(null);
  const markersRef = useRef([]);
  const infoWindowRef = useRef(null);
  const fetchDebounceRef = useRef(null);
  const mapInitializedRef = useRef(false);
  const abortControllerRef = useRef(null);
  const currentFetchBoundsRef = useRef(null);
  const fetchCustomersRef = useRef(null);
  const updateMarkersInMapRef = useRef(null);
  const isSearchModeRef = useRef(false);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mapHtml, setMapHtml] = useState('');
  // const [mapLoadError, setMapLoadError] = useState(false);
  const mapLoadTimeoutRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearchDropdown, setShowSearchDropdown] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isSearchMode, setIsSearchMode] = useState(false);
  const [selectedState, setSelectedState] = useState(null);
  const [states, setStates] = useState([]);
  const [stateLoading, setStateLoading] = useState(false);
  const [filterMode, setFilterMode] = useState('state');

  const filterModeOptions = [
    { _id: 'state', name: isVn() ? 'City' : 'State' },
    { _id: 'customer', name: 'Customer' },
  ];

  useEffect(() => {
    isSearchModeRef.current = isSearchMode;
  }, [isSearchMode]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const searchDebounceRef = useRef(null);
  const selectedCustomerRef = useRef(null);
  const pendingFocusCustomerRef = useRef(null);
  const selectedStateRef = useRef(null);
  const isFocusingStateRef = useRef(false);

  const isVietnam = isVn();
  const dispatch = useDispatch();
  const countries = useSelector((state) => state.resources.countries);

  const getCustomerSearch = useApi(MsB2b.getCustomerSearch);

  const getMapCenter = useCallback(() => {
    return isVietnam ? VIETNAM_CENTER : INDONESIA_CENTER;
  }, [isVietnam]);

  const getMapZoom = useCallback(() => {
    return isVietnam ? DEFAULT_ZOOM_VN : DEFAULT_ZOOM_ID;
  }, [isVietnam]);

  const initializeMapInstance = useCallback(
    (mapContainer, center, zoom, customersList = []) => {
      if (!mapContainer || mapRef.current) {
        return;
      }

      try {
        mapRef.current = new window.google.maps.Map(mapContainer, {
          center: center,
          zoom: zoom,
          zoomControl: true,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          gestureHandling: 'auto',
        });

        setTimeout(() => {
          if (window.google && window.google.maps && mapRef.current) {
            window.google.maps.event.trigger(mapRef.current, 'resize');
          }
        }, 100);

        infoWindowRef.current = new window.google.maps.InfoWindow();

        let idleDebounceTimer = null;

        window.google.maps.event.addListener(
          mapRef.current,
          'dragstart',
          () => {
            if (fetchDebounceRef.current) {
              clearTimeout(fetchDebounceRef.current);
              fetchDebounceRef.current = null;
            }
          },
        );

        window.google.maps.event.addListener(
          mapRef.current,
          'zoom_changed',
          () => {
            if (fetchDebounceRef.current) {
              clearTimeout(fetchDebounceRef.current);
              fetchDebounceRef.current = null;
            }
          },
        );

        window.google.maps.event.addListener(mapRef.current, 'idle', () => {
          if (!mapRef.current || isSearchModeRef.current) {
            return;
          }

          if (isFocusingStateRef.current) {
            return;
          }

          if (idleDebounceTimer) {
            clearTimeout(idleDebounceTimer);
          }

          idleDebounceTimer = setTimeout(() => {
            if (!mapRef.current || isSearchModeRef.current) {
              return;
            }

            const bounds = mapRef.current.getBounds();
            const currentZoom = mapRef.current.getZoom();
            const currentCenter = mapRef.current.getCenter();

            if (bounds) {
              setCurrentViewport({
                bounds: {
                  north: bounds.getNorthEast().lat(),
                  south: bounds.getSouthWest().lat(),
                  east: bounds.getNorthEast().lng(),
                  west: bounds.getSouthWest().lng(),
                },
                center: {
                  lat: currentCenter.lat(),
                  lng: currentCenter.lng(),
                },
                zoom: currentZoom,
              });

              if (fetchCustomersRef.current) {
                if (fetchDebounceRef.current) {
                  clearTimeout(fetchDebounceRef.current);
                }

                fetchDebounceRef.current = setTimeout(() => {
                  if (fetchCustomersRef.current) {
                    fetchCustomersRef.current(
                      {
                        north: bounds.getNorthEast().lat(),
                        south: bounds.getSouthWest().lat(),
                        east: bounds.getNorthEast().lng(),
                        west: bounds.getSouthWest().lng(),
                      },
                      currentZoom,
                    );
                  }
                  fetchDebounceRef.current = null;
                }, 300);
              }
            }
          }, 500);
        });

        mapInitializedRef.current = true;

        if (customersList.length > 0 && updateMarkersInMapRef.current) {
          updateMarkersInMapRef.current(customersList);
        }
      } catch (error) {
        console.error('Error initializing map:', error);
      }
    },
    [],
  );

  const initializeWebMap = useCallback(
    (center, zoom, customersList = []) => {
      if (
        (!isWeb && !isMobileWeb) ||
        isNative ||
        typeof window === 'undefined'
      ) {
        return;
      }

      if (mapRef.current || mapInitializedRef.current) {
        return;
      }

      const mapContainer = document.getElementById('customer-pinpoint-map');
      if (!mapContainer) {
        console.warn('Map container not found');
        return;
      }

      const mapKey = getGoogleMapKey() || '';

      if (!window.google || !window.google.maps) {
        if (!window.customerPinpointMapScriptLoading) {
          window.customerPinpointMapScriptLoading = true;
          const script = document.createElement('script');
          script.src = `https://maps.googleapis.com/maps/api/js?key=${mapKey}&callback=initCustomerPinpointMap&v=weekly`;
          script.async = true;
          script.defer = true;
          script.onerror = () => {
            window.customerPinpointMapScriptLoading = false;
            console.error('Failed to load Google Maps script');
          };

          window.initCustomerPinpointMap = () => {
            window.customerPinpointMapScriptLoading = false;
            if (mapContainer && !mapRef.current) {
              if (
                mapContainer.offsetWidth === 0 ||
                mapContainer.offsetHeight === 0
              ) {
                setTimeout(() => {
                  if (mapContainer && !mapRef.current) {
                    initializeMapInstance(
                      mapContainer,
                      center,
                      zoom,
                      customersList,
                    );
                  }
                }, 200);
                return;
              }

              initializeMapInstance(mapContainer, center, zoom, customersList);
            }
          };

          document.head.appendChild(script);
        }
        return;
      }

      if (!mapRef.current && mapContainer) {
        if (mapContainer.offsetWidth === 0 || mapContainer.offsetHeight === 0) {
          setTimeout(() => {
            if (mapContainer && !mapRef.current) {
              initializeMapInstance(mapContainer, center, zoom, customersList);
            }
          }, 100);
          return;
        }

        initializeMapInstance(mapContainer, center, zoom, customersList);
      } else if (mapRef.current) {
        const currentCenter = mapRef.current.getCenter();
        const currentZoom = mapRef.current.getZoom();

        if (
          Math.abs(currentCenter.lat() - center.lat) > 0.0001 ||
          Math.abs(currentCenter.lng() - center.lng) > 0.0001 ||
          Math.abs(currentZoom - zoom) > 0.1
        ) {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(zoom);
        }
      }
    },
    [initializeMapInstance],
  );

  const searchCustomers = useCallback(
    async (query) => {
      if (!query || query.trim().length < 2) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        return;
      }

      setSearchLoading(true);
      setIsSearchMode(true);

      setCustomers([]);
      setCurrentViewport(null);
      try {
        const filter = {
          keyword: query.trim(),
        };

        const payload = {
          params: {
            filter: filter,
            limit: 15,
            fields:
              '_id,name,code,delivery_address.lat_long.latitude,delivery_address.lat_long.longitude,delivery_address.name,delivery_address.street,delivery_address.street2,delivery_address.district.name,delivery_address.city.name,delivery_address.province.name,delivery_address.state.name,delivery_address.postal_code,delivery_address.country.name',
          },
        };

        const response = await getCustomerSearch.request(payload);
        if (response?.status === 200 && response?.data?.data) {
          setSearchResults(response.data.data);
          setShowSearchDropdown(true);
        } else {
          setSearchResults([]);
          setShowSearchDropdown(false);
        }
      } catch (error) {
        console.error('Error searching customers:', error);
        setSearchResults([]);
        setShowSearchDropdown(false);
      } finally {
        setSearchLoading(false);
      }
    },
    [getCustomerSearch],
  );

  const handleSearchChange = useCallback(
    (text) => {
      setSearchQuery(text);

      if (selectedCustomer) {
        setSelectedCustomer(null);
      }

      if (selectedState) {
        setSelectedState(null);
        selectedStateRef.current = null;
      }

      if (searchDebounceRef.current) {
        clearTimeout(searchDebounceRef.current);
      }

      if (text.trim().length < 2) {
        setSearchResults([]);
        setShowSearchDropdown(false);
        setIsSearchMode(false);
        return;
      }

      setIsSearchMode(true);
      searchDebounceRef.current = setTimeout(() => {
        searchCustomers(text);
      }, 800);
    },
    [searchCustomers, selectedCustomer, selectedState],
  );

  const moveMapToLocation = useCallback((validAddresses) => {
    if ((isWeb || isMobileWeb) && !isNative) {
      if (!mapRef.current || !mapInitializedRef.current) {
        return false;
      }

      const lats = validAddresses.map((addr) => addr.lat_long.latitude);
      const lngs = validAddresses.map((addr) => addr.lat_long.longitude);

      const minLat = Math.min(...lats);
      const maxLat = Math.max(...lats);
      const minLng = Math.min(...lngs);
      const maxLng = Math.max(...lngs);

      const centerLat = (minLat + maxLat) / 2;
      const centerLng = (minLng + maxLng) / 2;

      const latDiff = maxLat - minLat;
      const lngDiff = maxLng - minLng;
      const maxDiff = Math.max(latDiff, lngDiff);

      let zoom = 15;
      if (maxDiff > 0.1) {
        zoom = 10;
      } else if (maxDiff > 0.05) {
        zoom = 12;
      } else if (maxDiff > 0.01) {
        zoom = 14;
      }

      const addresses = validAddresses.map((addr) => ({
        lat: addr.lat_long.latitude,
        lng: addr.lat_long.longitude,
      }));

      if (mapRef.current) {
        mapRef.current.setCenter({ lat: centerLat, lng: centerLng });
        mapRef.current.setZoom(zoom);

        if (validAddresses.length > 1) {
          setTimeout(() => {
            const bounds = new window.google.maps.LatLngBounds();
            addresses.forEach((addr) => {
              bounds.extend(new window.google.maps.LatLng(addr.lat, addr.lng));
            });
            mapRef.current.fitBounds(bounds, {
              top: 50,
              right: 50,
              bottom: 50,
              left: 50,
            });
          }, 100);
        }
      }

      return true;
    }

    if (!webViewRef.current || !mapInitializedRef.current) {
      return false;
    }

    const lats = validAddresses.map((addr) => addr.lat_long.latitude);
    const lngs = validAddresses.map((addr) => addr.lat_long.longitude);

    const minLat = Math.min(...lats);
    const maxLat = Math.max(...lats);
    const minLng = Math.min(...lngs);
    const maxLng = Math.max(...lngs);

    const centerLat = (minLat + maxLat) / 2;
    const centerLng = (minLng + maxLng) / 2;

    const latDiff = maxLat - minLat;
    const lngDiff = maxLng - minLng;
    const maxDiff = Math.max(latDiff, lngDiff);

    let zoom = 15;
    if (maxDiff > 0.1) {
      zoom = 10;
    } else if (maxDiff > 0.05) {
      zoom = 12;
    } else if (maxDiff > 0.01) {
      zoom = 14;
    }

    const addresses = validAddresses.map((addr) => ({
      lat: addr.lat_long.latitude,
      lng: addr.lat_long.longitude,
    }));

    try {
      const message = JSON.stringify({
        type: 'focus_map',
        center: { lat: centerLat, lng: centerLng },
        zoom: zoom,
        addresses: addresses,
      });

      if (webViewRef.current.postMessage) {
        webViewRef.current.postMessage(message);
      }
    } catch (error) {
      console.error('Error posting message:', error);
    }

    const script = `
      (function() {
        try {
          if (typeof focusMapToLocation === 'function') {
            focusMapToLocation(
              { lat: ${centerLat}, lng: ${centerLng} },
              ${zoom},
              ${JSON.stringify(addresses)}
            );
          } else if (typeof map !== 'undefined' && map) {
            var center = { lat: ${centerLat}, lng: ${centerLng} };
            map.panTo(center);
            map.setZoom(${zoom});
            ${
              validAddresses.length > 1
                ? `
              setTimeout(function() {
                var bounds = new google.maps.LatLngBounds();
                ${validAddresses
                  .map(
                    (addr) =>
                      `bounds.extend(new google.maps.LatLng(${addr.lat_long.latitude}, ${addr.lat_long.longitude}));`,
                  )
                  .join('\n')}
                map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
              }, 100);
              `
                : ''
            }
          }
        } catch (e) {
          console.error('Error focusing map:', e);
        }
      })();
    `;

    try {
      if (webViewRef.current.injectJavaScript) {
        webViewRef.current.injectJavaScript(script);
      }
    } catch (error) {
      console.error('Error injecting script:', error);
    }

    return true;
  }, []);

  const focusMapToCustomer = useCallback(
    (customer) => {
      const deliveryAddresses = customer.delivery_address || [];
      const validAddresses = deliveryAddresses.filter(
        (address) =>
          address?.lat_long?.latitude && address?.lat_long?.longitude,
      );

      if (validAddresses.length === 0) {
        Toast.show({
          text1: 'No pinpoint available',
          type: 'error',
        });
        return;
      }

      selectedCustomerRef.current = customer;
      pendingFocusCustomerRef.current = customer;

      setCustomers([customer]);
      setSearchResults([]);
      setShowSearchDropdown(false);
      setIsSearchMode(true);
      setSelectedCustomer(customer);
      setSearchQuery(customer.name || '');

      if (mapInitializedRef.current) {
        const moveMap = () => {
          moveMapToLocation(validAddresses);
        };
        moveMap();
        setTimeout(moveMap, 100);
        setTimeout(moveMap, 300);
        setTimeout(moveMap, 600);
      }
    },
    [moveMapToLocation],
  );

  const updateMarkersInMap = useCallback((customersList) => {
    if ((isWeb || isMobileWeb) && !isNative && typeof window !== 'undefined') {
      if (!mapRef.current || !window.google || !window.google.maps) {
        return;
      }

      markersRef.current.forEach((marker) => {
        marker.setMap(null);
      });
      markersRef.current = [];

      const markersData = customersList.flatMap((customer) => {
        const customerId = customer._id || '';
        const name = customer.name || '';
        const deliveryAddresses = customer.delivery_address || [];

        return deliveryAddresses
          .map((deliveryAddress, index) => {
            if (
              !deliveryAddress?.lat_long?.latitude ||
              !deliveryAddress?.lat_long?.longitude
            ) {
              return null;
            }

            const addressParts = [];
            if (deliveryAddress.street) {
              addressParts.push(deliveryAddress.street);
            }
            if (deliveryAddress.street2) {
              addressParts.push(deliveryAddress.street2);
            }
            if (deliveryAddress.district?.name) {
              addressParts.push(deliveryAddress.district.name);
            }
            if (deliveryAddress.city?.name) {
              addressParts.push(deliveryAddress.city.name);
            }
            if (deliveryAddress.province?.name) {
              addressParts.push(deliveryAddress.province.name);
            }
            if (deliveryAddress.state?.name) {
              addressParts.push(deliveryAddress.state.name);
            }
            if (deliveryAddress.postal_code) {
              addressParts.push(deliveryAddress.postal_code);
            }
            if (deliveryAddress.country?.name) {
              addressParts.push(deliveryAddress.country.name);
            }
            const fullAddress =
              addressParts.length > 0 ? addressParts.join(', ') : '';

            return {
              id: `${String(customerId)}_${index}`,
              lat: deliveryAddress.lat_long.latitude,
              lng: deliveryAddress.lat_long.longitude,
              name: name,
              addressName: deliveryAddress.name || '',
              fullAddress: fullAddress,
            };
          })
          .filter(Boolean);
      });

      markersData.forEach((markerData) => {
        const marker = new window.google.maps.Marker({
          position: { lat: markerData.lat, lng: markerData.lng },
          map: mapRef.current,
          title: markerData.name || 'Unknown Customer',
        });

        const contentString =
          '<div style="padding: 10px; min-width: 200px;">' +
          '<div style="margin-bottom: 8px;"><b style="font-size: 14px;">' +
          (markerData.name || 'Unknown Customer').replace(/'/g, "\\'") +
          '</b></div>' +
          (markerData.fullAddress
            ? '<div style="margin-bottom: 8px; color: #666; font-size: 12px; line-height: 1.4;">' +
              markerData.fullAddress.replace(/'/g, "\\'") +
              '</div>'
            : markerData.addressName
            ? '<div style="margin-bottom: 8px; color: #666; font-size: 12px;">' +
              markerData.addressName.replace(/'/g, "\\'") +
              '</div>'
            : '') +
          '<div style="margin-top: 10px;">' +
          '<a href="#" id="open-gmaps-' +
          markerData.id +
          '" style="color: #1976d2; text-decoration: none; font-size: 12px; font-weight: 500;">' +
          'Click here to open in Google Maps' +
          '</a>' +
          '</div>' +
          '</div>';

        marker.addListener('click', () => {
          if (infoWindowRef.current) {
            infoWindowRef.current.setContent(contentString);
            infoWindowRef.current.open(mapRef.current, marker);

            setTimeout(() => {
              const linkElement = document.getElementById(
                'open-gmaps-' + markerData.id,
              );
              if (linkElement) {
                linkElement.addEventListener('click', (e) => {
                  e.preventDefault();
                  const gmapsUrl = generateBetterGMapsLink(
                    markerData.lat,
                    markerData.lng,
                    markerData.fullAddress || '',
                  );
                  if (gmapsUrl) {
                    window.open(gmapsUrl, '_blank');
                  }
                });
              }
            }, 100);
          }
        });

        markersRef.current.push(marker);
      });

      return;
    }

    if (!webViewRef.current) {
      return;
    }

    if (!mapInitializedRef.current) {
      return;
    }

    const markersData = customersList.flatMap((customer) => {
      const customerId = customer._id || '';
      const name = customer.name || '';
      const deliveryAddresses = customer.delivery_address || [];

      return deliveryAddresses
        .map((deliveryAddress, index) => {
          if (
            !deliveryAddress?.lat_long?.latitude ||
            !deliveryAddress?.lat_long?.longitude
          ) {
            return null;
          }

          const addressParts = [];
          if (deliveryAddress.street) {
            addressParts.push(deliveryAddress.street);
          }
          if (deliveryAddress.street2) {
            addressParts.push(deliveryAddress.street2);
          }
          if (deliveryAddress.district?.name) {
            addressParts.push(deliveryAddress.district.name);
          }
          if (deliveryAddress.city?.name) {
            addressParts.push(deliveryAddress.city.name);
          }
          if (deliveryAddress.province?.name) {
            addressParts.push(deliveryAddress.province.name);
          }
          if (deliveryAddress.state?.name) {
            addressParts.push(deliveryAddress.state.name);
          }
          if (deliveryAddress.postal_code) {
            addressParts.push(deliveryAddress.postal_code);
          }
          if (deliveryAddress.country?.name) {
            addressParts.push(deliveryAddress.country.name);
          }
          const fullAddress =
            addressParts.length > 0 ? addressParts.join(', ') : '';

          return {
            id: `${String(customerId)}_${index}`,
            lat: deliveryAddress.lat_long.latitude,
            lng: deliveryAddress.lat_long.longitude,
            name: name,
            addressName: deliveryAddress.name || '',
            fullAddress: fullAddress,
          };
        })
        .filter(Boolean);
    });

    try {
      const message = JSON.stringify({
        type: 'update_markers',
        markers: markersData,
      });

      if (webViewRef.current.postMessage) {
        webViewRef.current.postMessage(message);
        return;
      }

      const script = `
        (function() {
          try {
            if (typeof updateMapMarkers === 'function') {
              updateMapMarkers(${JSON.stringify(markersData)});
            } else if (window.updateMapMarkers) {
              window.updateMapMarkers(${JSON.stringify(markersData)});
            } else {
              window.postMessage(${JSON.stringify(message)}, '*');
            }
          } catch (e) {
            console.error('Error in updateMarkers script:', e);
          }
        })();
      `;
      webViewRef.current.injectJavaScript(script);
    } catch (error) {
      console.error('Error updating markers:', error);
    }
  }, []);

  const fetchCustomers = useCallback(
    async (bounds = null, zoom = null) => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }

      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      currentFetchBoundsRef.current = bounds;

      setLoading(true);

      if (webViewRef.current) {
        try {
          const script = `
            (function() {
              if (typeof setMapInteractionEnabled === 'function') {
                setMapInteractionEnabled(false);
              }
            })();
          `;
          webViewRef.current.injectJavaScript(script);
        } catch (e) {
          console.error('Error disabling map interaction:', e);
        }
      }

      try {
        const filter = {};

        if (bounds) {
          filter.$and = [
            {
              'delivery_address.lat_long.latitude': {
                $exists: true,
                $ne: null,
              },
              'delivery_address.lat_long.longitude': {
                $exists: true,
                $ne: null,
              },
            },
            {
              'delivery_address.lat_long.latitude': {
                $gte: bounds.south,
                $lte: bounds.north,
              },
            },
            {
              'delivery_address.lat_long.longitude': {
                $gte: bounds.west,
                $lte: bounds.east,
              },
            },
          ];
        } else {
          filter['delivery_address.lat_long.latitude'] = {
            $exists: true,
            $ne: null,
          };
          filter['delivery_address.lat_long.longitude'] = {
            $exists: true,
            $ne: null,
          };
        }

        const payload = {
          params: {
            filter: filter,
            limit: 20,
            fields:
              '_id,name,code,delivery_address.lat_long.latitude,delivery_address.lat_long.longitude,delivery_address.name,delivery_address.street,delivery_address.street2,delivery_address.district.name,delivery_address.city.name,delivery_address.province.name,delivery_address.state.name,delivery_address.postal_code,delivery_address.country.name',
          },
        };

        const response = await getCustomers(payload);

        if (abortController.signal.aborted) {
          setLoading(false);
          return;
        }

        if (bounds && currentFetchBoundsRef.current) {
          const savedBounds = currentFetchBoundsRef.current;
          const northDiff =
            Math.abs(savedBounds.north - bounds.north) /
            Math.abs(savedBounds.north);
          const southDiff =
            Math.abs(savedBounds.south - bounds.south) /
            Math.abs(savedBounds.south);
          const eastDiff =
            Math.abs(savedBounds.east - bounds.east) /
            Math.abs(savedBounds.east);
          const westDiff =
            Math.abs(savedBounds.west - bounds.west) /
            Math.abs(savedBounds.west);

          if (
            northDiff > 0.01 ||
            southDiff > 0.01 ||
            eastDiff > 0.01 ||
            westDiff > 0.01
          ) {
            setLoading(false);
            if (webViewRef.current) {
              try {
                const script = `
                  (function() {
                    if (typeof setMapInteractionEnabled === 'function') {
                      setMapInteractionEnabled(true);
                    }
                  })();
                `;
                webViewRef.current.injectJavaScript(script);
              } catch (e) {
                console.error('Error enabling map interaction:', e);
              }
            }
            return;
          }
        }

        if (response?.data?.data) {
          const validCustomers = response.data.data.filter((customer) => {
            const deliveryAddresses = customer.delivery_address || [];
            return deliveryAddresses.some(
              (address) =>
                address?.lat_long?.latitude && address?.lat_long?.longitude,
            );
          });

          if (validCustomers.length === 0) {
            setCustomers([]);
            if (isNative && updateMarkersInMapRef.current) {
              updateMarkersInMapRef.current([]);
            }
            return;
          }

          setCustomers(validCustomers);

          if (isNative && updateMarkersInMapRef.current) {
            updateMarkersInMapRef.current(validCustomers);
          }
        } else {
          setCustomers([]);
          if (isNative && updateMarkersInMapRef.current) {
            updateMarkersInMapRef.current([]);
          }
        }
      } catch (error) {
        if (error.name === 'AbortError' || abortController.signal.aborted) {
          setLoading(false);
          return;
        }
        console.error('Error fetching customers:', error);
        setCustomers([]);
      } finally {
        if (
          abortControllerRef.current === abortController &&
          !abortController.signal.aborted
        ) {
          setLoading(false);

          if (webViewRef.current) {
            try {
              const script = `
                (function() {
                  if (typeof setMapInteractionEnabled === 'function') {
                    setMapInteractionEnabled(true);
                  }
                })();
              `;
              webViewRef.current.injectJavaScript(script);
            } catch (e) {
              console.error('Error enabling map interaction:', e);
            }
          }
        }
      }
    },
    [isVietnam],
  );

  useEffect(() => {
    fetchCustomersRef.current = fetchCustomers;
  }, [fetchCustomers]);

  useEffect(() => {
    updateMarkersInMapRef.current = updateMarkersInMap;
  }, [updateMarkersInMap]);

  const handleClearSearch = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setShowSearchDropdown(false);
    setIsSearchMode(false);
    setCustomers([]);
    setCurrentViewport(null);
    setSelectedCustomer(null);
    setSelectedState(null);
    selectedStateRef.current = null;
    fetchCustomers(null, null);
  }, [fetchCustomers]);

  useEffect(() => {
    let isMounted = true;
    const loadCountries = async () => {
      if (!countries?.length) {
        try {
          await dispatch(Resources.getCountries({}));
        } catch (error) {
          console.error('Error loading countries:', error);
        }
      }
    };
    if (isMounted) {
      loadCountries();
    }
    return () => {
      isMounted = false;
    };
  }, [dispatch, countries?.length]);

  useEffect(() => {
    let isMounted = true;
    const loadStates = async () => {
      if (countries?.length && !states.length && isMounted) {
        try {
          setStateLoading(true);
          const cn = countries.find(
            (x) => x.name.toLowerCase() === defaultCountry(),
          );
          if (cn && cn._id) {
            const response = await getAllStates({
              params: {
                filter: { country_id: cn._id },
                sort: JSON.stringify({ name: 1 }),
              },
            });
            if (
              isMounted &&
              response?.data?.data &&
              Array.isArray(response.data.data)
            ) {
              setStates(response.data.data);
            } else if (isMounted) {
              console.warn(
                'States data is not an array:',
                response?.data?.data,
              );
            }
          } else if (isMounted) {
            console.warn(
              'Country not found or missing _id. Countries:',
              countries,
              'Default country:',
              defaultCountry(),
            );
          }
        } catch (error) {
          console.error('Error fetching states:', error);
          if (isMounted) {
            Toast.show({
              text1: 'Error loading states',
              text2: error?.message || 'Please try again',
              type: 'error',
            });
          }
        } finally {
          if (isMounted) {
            setStateLoading(false);
          }
        }
      }
    };
    if (countries?.length) {
      loadStates();
    }
    return () => {
      isMounted = false;
    };
  }, [countries, states.length]);

  const normalizeStateName = useCallback((stateName) => {
    if (!stateName) {
      return stateName;
    }

    let normalized = stateName.trim();

    const stateNameMappings = {
      'DKI Jakarta': 'South Jakarta',
      'DI Yogyakarta': 'Yogyakarta',
      'Nusa Tenggara Timur': 'NTT',
      'Nusa Tenggara Barat': 'West Nusa Tenggara',
      'Nanggroe Aceh Darussalam': 'Aceh',
    };

    if (stateNameMappings[normalized]) {
      return stateNameMappings[normalized];
    }

    normalized = normalized.replace(/\s*\([^)]*\)\s*/g, '').trim();

    if (stateNameMappings[normalized]) {
      return stateNameMappings[normalized];
    }

    return normalized;
  }, []);

  const handleStateChange = useCallback(
    async (selectedStateValue) => {
      if (!selectedStateValue) {
        setSelectedState(null);
        selectedStateRef.current = null;
        setCustomers([]);
        setCurrentViewport(null);
        setIsSearchMode(false);
        setSearchQuery('');
        setSearchResults([]);
        setShowSearchDropdown(false);
        const center = getMapCenter();
        const zoom = getMapZoom();
        if ((isWeb || isMobileWeb) && !isNative && mapRef.current) {
          mapRef.current.setCenter(center);
          mapRef.current.setZoom(zoom);
        }
        fetchCustomers(null, null);
        return;
      }

      setSelectedState(selectedStateValue);
      selectedStateRef.current = selectedStateValue;
      setIsSearchMode(false);
      setSearchQuery('');
      setSearchResults([]);
      setShowSearchDropdown(false);
      setCustomers([]);
      setCurrentViewport(null);

      try {
        const stateName = normalizeStateName(selectedStateValue.name);
        const countryName = isVietnam ? 'Vietnam' : 'Indonesia';
        const addressQuery = isVietnam
          ? `${stateName}, ${countryName}`
          : `${stateName} Province, ${countryName}`;

        const MAX_PROVINCE_ZOOM = 11;

        let stateCenter = null;
        let stateZoom = 11;
        let geocodeViewport = null;

        try {
          const autocompleteResponse = await googleMapsAutocomplete({
            params: {
              filter: {
                input: addressQuery,
              },
            },
          });

          const predictions = autocompleteResponse?.data?.data?.predictions;
          if (predictions && predictions.length > 0) {
            let selectedPrediction = predictions[0];
            const provincePrediction = predictions.find(
              (pred) =>
                pred.types &&
                (pred.types.includes('administrative_area_level_1') ||
                  pred.types.includes('political')),
            );
            if (provincePrediction) {
              selectedPrediction = provincePrediction;
            }

            const placeId = selectedPrediction.place_id;
            const placeDetailResponse = await googleMapsPlaceDetail(placeId);
            const placeDetail = placeDetailResponse?.data?.data?.result;

            if (placeDetail?.geometry) {
              const location = placeDetail.geometry.location;
              stateCenter = {
                lat: location.lat,
                lng: location.lng,
              };

              const viewport = placeDetail.geometry.viewport;
              if (viewport) {
                const latDiff = viewport.northeast.lat - viewport.southwest.lat;
                const lngDiff = viewport.northeast.lng - viewport.southwest.lng;
                const maxDiff = Math.max(latDiff, lngDiff);

                if (maxDiff < 0.1) {
                  if (placeDetail.geometry.bounds) {
                    geocodeViewport = {
                      northeast: {
                        lat: placeDetail.geometry.bounds.northeast.lat,
                        lng: placeDetail.geometry.bounds.northeast.lng,
                      },
                      southwest: {
                        lat: placeDetail.geometry.bounds.southwest.lat,
                        lng: placeDetail.geometry.bounds.southwest.lng,
                      },
                    };
                    const boundsLatDiff =
                      geocodeViewport.northeast.lat -
                      geocodeViewport.southwest.lat;
                    const boundsLngDiff =
                      geocodeViewport.northeast.lng -
                      geocodeViewport.southwest.lng;
                    const boundsMaxDiff = Math.max(
                      boundsLatDiff,
                      boundsLngDiff,
                    );

                    if (boundsMaxDiff > 2) {
                      stateZoom = MAX_PROVINCE_ZOOM;
                    } else if (boundsMaxDiff > 1) {
                      stateZoom = MAX_PROVINCE_ZOOM;
                    } else if (boundsMaxDiff > 0.5) {
                      stateZoom = MAX_PROVINCE_ZOOM;
                    } else {
                      stateZoom = MAX_PROVINCE_ZOOM;
                    }
                  } else {
                    stateZoom = 11;
                    geocodeViewport = viewport;
                  }
                } else {
                  geocodeViewport = viewport;
                  if (maxDiff > 2) {
                    stateZoom = MAX_PROVINCE_ZOOM;
                  } else if (maxDiff > 1) {
                    stateZoom = MAX_PROVINCE_ZOOM;
                  } else if (maxDiff > 0.5) {
                    stateZoom = MAX_PROVINCE_ZOOM;
                  } else if (maxDiff > 0.2) {
                    stateZoom = MAX_PROVINCE_ZOOM;
                  } else {
                    stateZoom = MAX_PROVINCE_ZOOM;
                  }
                }
              } else {
                stateZoom = MAX_PROVINCE_ZOOM;
              }
            } else {
              console.warn('Place detail missing geometry');
              stateCenter = getMapCenter();
              stateZoom = getMapZoom();
            }
          } else {
            console.warn('No predictions found for address:', addressQuery);
            stateCenter = getMapCenter();
            stateZoom = getMapZoom();
          }
        } catch (geocodeError) {
          console.error('Error calling Google Maps API:', geocodeError);
          stateCenter = getMapCenter();
          stateZoom = getMapZoom();
        }

        const focusMapToState = () => {
          if ((isWeb || isMobileWeb) && !isNative) {
            if (
              mapRef.current &&
              mapInitializedRef.current &&
              window.google &&
              window.google.maps
            ) {
              if (geocodeViewport) {
                const bounds = new window.google.maps.LatLngBounds(
                  new window.google.maps.LatLng(
                    geocodeViewport.southwest.lat,
                    geocodeViewport.southwest.lng,
                  ),
                  new window.google.maps.LatLng(
                    geocodeViewport.northeast.lat,
                    geocodeViewport.northeast.lng,
                  ),
                );
                mapRef.current.fitBounds(bounds, { padding: 5 });
                setTimeout(() => {
                  if (mapRef.current) {
                    const currentZoom = mapRef.current.getZoom();
                    if (currentZoom > MAX_PROVINCE_ZOOM) {
                      mapRef.current.setZoom(MAX_PROVINCE_ZOOM);
                    }
                  }
                }, 500);
              } else {
                mapRef.current.setCenter(stateCenter);
                mapRef.current.setZoom(stateZoom);
              }
            } else {
              const checkMap = setInterval(() => {
                if (
                  mapRef.current &&
                  mapInitializedRef.current &&
                  window.google &&
                  window.google.maps
                ) {
                  clearInterval(checkMap);
                  if (geocodeViewport) {
                    const bounds = new window.google.maps.LatLngBounds(
                      new window.google.maps.LatLng(
                        geocodeViewport.southwest.lat,
                        geocodeViewport.southwest.lng,
                      ),
                      new window.google.maps.LatLng(
                        geocodeViewport.northeast.lat,
                        geocodeViewport.northeast.lng,
                      ),
                    );
                    mapRef.current.fitBounds(bounds, { padding: 5 });
                    setTimeout(() => {
                      if (mapRef.current) {
                        const currentZoom = mapRef.current.getZoom();
                        if (currentZoom > MAX_PROVINCE_ZOOM) {
                          mapRef.current.setZoom(MAX_PROVINCE_ZOOM);
                        }
                      }
                    }, 500);
                  } else {
                    mapRef.current.setCenter(stateCenter);
                    mapRef.current.setZoom(stateZoom);
                  }
                }
              }, 100);
              setTimeout(() => clearInterval(checkMap), 5000);
            }
          } else if (webViewRef.current && mapInitializedRef.current) {
            const viewportBounds = geocodeViewport
              ? {
                  northeast: {
                    lat: geocodeViewport.northeast.lat,
                    lng: geocodeViewport.northeast.lng,
                  },
                  southwest: {
                    lat: geocodeViewport.southwest.lat,
                    lng: geocodeViewport.southwest.lng,
                  },
                }
              : null;

            const message = JSON.stringify({
              type: 'focus_map',
              center: stateCenter,
              zoom: stateZoom,
              addresses: [],
              viewport: viewportBounds,
            });

            if (webViewRef.current.postMessage) {
              webViewRef.current.postMessage(message);
            }

            const script = geocodeViewport
              ? `
              (function() {
                try {
                  var MAX_PROVINCE_ZOOM = ${MAX_PROVINCE_ZOOM};
                  if (typeof map !== 'undefined' && map) {
                    var bounds = new google.maps.LatLngBounds(
                      new google.maps.LatLng(${geocodeViewport.southwest.lat}, ${geocodeViewport.southwest.lng}),
                      new google.maps.LatLng(${geocodeViewport.northeast.lat}, ${geocodeViewport.northeast.lng})
                    );
                    map.fitBounds(bounds, { padding: 5 });
                    setTimeout(function() {
                      if (map) {
                        var currentZoom = map.getZoom();
                        if (currentZoom > MAX_PROVINCE_ZOOM) {
                          map.setZoom(MAX_PROVINCE_ZOOM);
                        }
                      }
                    }, 500);
                  } else if (typeof focusMapToLocation === 'function') {
                    var bounds = new google.maps.LatLngBounds(
                      new google.maps.LatLng(${geocodeViewport.southwest.lat}, ${geocodeViewport.southwest.lng}),
                      new google.maps.LatLng(${geocodeViewport.northeast.lat}, ${geocodeViewport.northeast.lng})
                    );
                    if (map) {
                      map.fitBounds(bounds, { padding: 5 });
                      setTimeout(function() {
                        if (map) {
                          var currentZoom = map.getZoom();
                          if (currentZoom > MAX_PROVINCE_ZOOM) {
                            map.setZoom(MAX_PROVINCE_ZOOM);
                          }
                        }
                      }, 500);
                    }
                  }
                } catch (e) {
                  console.error('Error focusing map:', e);
                }
              })();
            `
              : `
              (function() {
                try {
                  if (typeof focusMapToLocation === 'function') {
                    focusMapToLocation(
                      { lat: ${stateCenter.lat}, lng: ${stateCenter.lng} },
                      ${stateZoom},
                      []
                    );
                  } else if (typeof map !== 'undefined' && map) {
                    map.panTo({ lat: ${stateCenter.lat}, lng: ${stateCenter.lng} });
                    map.setZoom(${stateZoom});
                  }
                } catch (e) {
                  console.error('Error focusing map:', e);
                }
              })();
            `;

            try {
              if (webViewRef.current.injectJavaScript) {
                webViewRef.current.injectJavaScript(script);
              }
            } catch (error) {
              console.error('Error injecting script:', error);
            }
          }
        };

        isFocusingStateRef.current = true;

        if (fetchDebounceRef.current) {
          clearTimeout(fetchDebounceRef.current);
          fetchDebounceRef.current = null;
        }

        let focusCalled = false;
        const focusOnce = () => {
          if (focusCalled) {
            return;
          }
          focusCalled = true;
          focusMapToState();
        };

        setTimeout(() => {
          focusOnce();
        }, 300);

        setTimeout(() => {
          if (
            (isWeb || isMobileWeb) &&
            !isNative &&
            mapRef.current &&
            mapInitializedRef.current
          ) {
            const bounds = mapRef.current.getBounds();
            const currentZoom = mapRef.current.getZoom();
            if (bounds) {
              fetchCustomers(
                {
                  north: bounds.getNorthEast().lat(),
                  south: bounds.getSouthWest().lat(),
                  east: bounds.getNorthEast().lng(),
                  west: bounds.getSouthWest().lng(),
                },
                currentZoom,
              );
            } else {
              fetchCustomers(null, null);
            }
            isFocusingStateRef.current = false;
          } else if (isNative) {
            if (geocodeViewport) {
              const bounds = {
                north: geocodeViewport.northeast.lat,
                south: geocodeViewport.southwest.lat,
                east: geocodeViewport.northeast.lng,
                west: geocodeViewport.southwest.lng,
              };
              fetchCustomers(bounds, stateZoom);
            } else {
              fetchCustomers(null, null);
            }
            setTimeout(() => {
              isFocusingStateRef.current = false;
            }, 500);
          } else {
            fetchCustomers(null, null);
            isFocusingStateRef.current = false;
          }
        }, 2500);
      } catch (error) {
        console.error('Error handling state selection:', error);
        Toast.show({
          text1: 'Error focusing on state',
          type: 'error',
        });
      }
    },
    [
      isVietnam,
      getMapCenter,
      getMapZoom,
      fetchCustomers,
      isWeb,
      isMobileWeb,
      isNative,
      normalizeStateName,
    ],
  );

  const generateBetterGMapsLink = useCallback((lat, lng, fullAddress = '') => {
    if (!lat || !lng) {
      return '';
    }

    if (fullAddress && fullAddress.trim()) {
      const encodedAddress = encodeURIComponent(fullAddress.trim());
      return `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
    }

    return `https://www.google.com/maps/@${lat},${lng},15z`;
  }, []);

  const handleMarkerPress = useCallback(
    (customer) => {
      const deliveryAddress = customer.delivery_address?.[0];
      if (
        !deliveryAddress?.lat_long?.latitude ||
        !deliveryAddress?.lat_long?.longitude
      ) {
        return;
      }
      const lat = deliveryAddress.lat_long.latitude;
      const lng = deliveryAddress.lat_long.longitude;

      const addressParts = [];
      if (deliveryAddress.street) {
        addressParts.push(deliveryAddress.street);
      }
      if (deliveryAddress.street2) {
        addressParts.push(deliveryAddress.street2);
      }
      if (deliveryAddress.city?.name) {
        addressParts.push(deliveryAddress.city.name);
      }
      if (deliveryAddress.state?.name) {
        addressParts.push(deliveryAddress.state.name);
      }
      if (deliveryAddress.country?.name) {
        addressParts.push(deliveryAddress.country.name);
      }
      const fullAddress = addressParts.join(', ');

      const gmapsUrl = generateBetterGMapsLink(lat, lng, fullAddress);
      if (gmapsUrl) {
        Linking.openURL(gmapsUrl).catch((err) => {
          console.error('Error opening Google Maps:', err);
        });
      }
    },
    [generateBetterGMapsLink],
  );

  const handleWebViewMessage = useCallback(
    (event) => {
      try {
        const messageData = event?.data || event?.nativeEvent?.data;
        if (!messageData) {
          return;
        }

        const data = JSON.parse(messageData);

        if (data.type === 'open_google_maps') {
          const gmapsUrl = generateBetterGMapsLink(
            data.lat,
            data.lng,
            data.fullAddress || '',
          );
          if (gmapsUrl) {
            Linking.openURL(gmapsUrl).catch((err) => {
              console.error('Error opening Google Maps:', err);
            });
          }
        } else if (data.type === 'bounds_change') {
          setCurrentViewport({
            bounds: data.bounds,
            center: data.center,
            zoom: data.zoom,
          });

          if (isSearchMode) {
            return;
          }

          if (isFocusingStateRef.current) {
            return;
          }

          const bounds = data.bounds;

          if (fetchDebounceRef.current) {
            clearTimeout(fetchDebounceRef.current);
            fetchDebounceRef.current = null;
          }

          fetchDebounceRef.current = setTimeout(() => {
            fetchCustomers(bounds, data.zoom);
            fetchDebounceRef.current = null;
          }, 800);
        } else if (data.type === 'map_error') {
          console.error('Google Maps error:', data.error);
          // setMapLoadError(true);
          if (mapLoadTimeoutRef.current) {
            clearTimeout(mapLoadTimeoutRef.current);
            mapLoadTimeoutRef.current = null;
          }
        } else if (data.type === 'map_loaded') {
          // setMapLoadError(false);
          mapInitializedRef.current = true;
          if (mapLoadTimeoutRef.current) {
            clearTimeout(mapLoadTimeoutRef.current);
            mapLoadTimeoutRef.current = null;
          }

          if (pendingFocusCustomerRef.current) {
            const customer = pendingFocusCustomerRef.current;
            const deliveryAddresses = customer.delivery_address || [];
            const validAddresses = deliveryAddresses.filter(
              (address) =>
                address?.lat_long?.latitude && address?.lat_long?.longitude,
            );

            if (validAddresses.length > 0) {
              setTimeout(() => {
                const moveMap = () => {
                  moveMapToLocation(validAddresses);
                };
                moveMap();
                setTimeout(moveMap, 100);
                setTimeout(moveMap, 300);
                setTimeout(moveMap, 600);
                pendingFocusCustomerRef.current = null;
              }, 300);
            } else {
              pendingFocusCustomerRef.current = null;
            }
          }
        }
      } catch (error) {
        console.error('Error handling webview message:', error, event);
      }
    },
    [handleMarkerPress, fetchCustomers, isSearchMode, moveMapToLocation],
  );

  useEffect(() => {
    mapInitializedRef.current = false;
    setCustomers([]);
    setSelectedState(null);
    selectedStateRef.current = null;

    fetchCustomers(null, null, null);
  }, [isVietnam, fetchCustomers]);

  const [currentViewport, setCurrentViewport] = useState(null);
  const [shouldRegenerateMap, setShouldRegenerateMap] = useState(true);

  const googleMapKey = getGoogleMapKey() || '';

  useEffect(() => {
    if (!isNative && (isWeb || isMobileWeb) && typeof window !== 'undefined') {
      if (mapInitializedRef.current || mapRef.current) {
        return;
      }

      const center = getMapCenter();
      const zoom = getMapZoom();

      const checkAndInitialize = () => {
        const mapContainer = document.getElementById('customer-pinpoint-map');
        if (mapContainer) {
          const hasDimensions =
            mapContainer.offsetWidth > 0 && mapContainer.offsetHeight > 0;
          const computedStyle = window.getComputedStyle(mapContainer);
          const isVisible =
            computedStyle.display !== 'none' &&
            computedStyle.visibility !== 'hidden';

          if (hasDimensions && isVisible) {
            if (!mapRef.current && !mapInitializedRef.current) {
              initializeWebMap(center, zoom, []);
            }
          } else {
            if (checkAndInitialize.retryCount === undefined) {
              checkAndInitialize.retryCount = 0;
            }
            if (checkAndInitialize.retryCount < 10) {
              checkAndInitialize.retryCount++;
              setTimeout(checkAndInitialize, 100);
            } else {
              if (!mapRef.current && !mapInitializedRef.current) {
                if (mapContainer.offsetHeight === 0) {
                  mapContainer.style.height = `${mapHeight}px`;
                }
                initializeWebMap(center, zoom, []);
              }
            }
          }
        } else {
          if (checkAndInitialize.retryCount === undefined) {
            checkAndInitialize.retryCount = 0;
          }
          if (checkAndInitialize.retryCount < 10) {
            checkAndInitialize.retryCount++;
            setTimeout(checkAndInitialize, 100);
          }
        }
      };

      const timer = setTimeout(checkAndInitialize, 200);

      const handleResize = () => {
        if (mapRef.current && window.google && window.google.maps) {
          window.google.maps.event.trigger(mapRef.current, 'resize');
        }
      };
      window.addEventListener('resize', handleResize);

      return () => {
        clearTimeout(timer);
        window.removeEventListener('resize', handleResize);
      };
    }
  }, []);

  useEffect(() => {
    if (
      !isNative &&
      (isWeb || isMobileWeb) &&
      typeof window !== 'undefined' &&
      mapRef.current
    ) {
      const center = getMapCenter();
      const zoom = getMapZoom();
      mapRef.current.setCenter(center);
      mapRef.current.setZoom(zoom);
    }
  }, [isVietnam, getMapCenter, getMapZoom]);

  useEffect(() => {
    if (isNative) {
      if (!shouldRegenerateMap && mapInitializedRef.current) {
        return;
      }

      const hasViewport =
        currentViewport !== null &&
        currentViewport.center &&
        currentViewport.bounds;
      const shouldPreserve = mapInitializedRef.current && hasViewport;

      const center =
        shouldPreserve && currentViewport?.center
          ? currentViewport.center
          : getMapCenter();
      const zoom =
        shouldPreserve && currentViewport?.zoom
          ? currentViewport.zoom
          : getMapZoom();
      const bounds = currentViewport?.bounds || null;

      const html = generateMapScript(
        customers,
        center,
        zoom,
        shouldPreserve,
        bounds,
        currentViewport?.center || center,
        currentViewport?.zoom || zoom,
        googleMapKey,
      );
      setMapHtml(html);
      setShouldRegenerateMap(false);
    }
  }, [
    shouldRegenerateMap,
    isVietnam,
    currentViewport,
    getMapCenter,
    getMapZoom,
    googleMapKey,
    customers,
  ]);

  useEffect(() => {
    if (mapInitializedRef.current) {
      const timer = setTimeout(() => {
        if ((isWeb || isMobileWeb) && !isNative) {
          if (updateMarkersInMapRef.current) {
            updateMarkersInMapRef.current(customers);
          }
        } else {
          updateMarkersInMap(customers);
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [customers, updateMarkersInMap]);

  useEffect(() => {
    if (
      selectedCustomerRef.current &&
      isSearchMode &&
      customers.length === 1 &&
      mapInitializedRef.current
    ) {
      const customer = selectedCustomerRef.current;

      const deliveryAddresses = customer.delivery_address || [];
      const validAddresses = deliveryAddresses.filter(
        (address) =>
          address?.lat_long?.latitude && address?.lat_long?.longitude,
      );

      if (validAddresses.length === 0) {
        selectedCustomerRef.current = null;
        pendingFocusCustomerRef.current = null;
        return;
      }

      const moveMap = () => {
        moveMapToLocation(validAddresses);
      };

      moveMap();
      setTimeout(moveMap, 100);
      setTimeout(moveMap, 300);
      setTimeout(moveMap, 600);

      setTimeout(() => {
        selectedCustomerRef.current = null;
        pendingFocusCustomerRef.current = null;
      }, 1000);
    }
  }, [customers, isSearchMode, moveMapToLocation]);

  useEffect(() => {
    setCurrentViewport(null);
    mapInitializedRef.current = false;
    setCustomers([]);
    // setMapLoadError(false);
    setShouldRegenerateMap(true);
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    if (fetchDebounceRef.current) {
      clearTimeout(fetchDebounceRef.current);
    }
    if (mapLoadTimeoutRef.current) {
      clearTimeout(mapLoadTimeoutRef.current);
      mapLoadTimeoutRef.current = null;
    }

    mapLoadTimeoutRef.current = setTimeout(() => {
      if (!mapInitializedRef.current) {
        // setMapLoadError(true);
      }
    }, 20000);

    return () => {
      if (mapLoadTimeoutRef.current) {
        clearTimeout(mapLoadTimeoutRef.current);
      }
    };
  }, [isVietnam]);

  const mapHeight =
    isWeb || isMobileWeb
      ? isMobileWeb
        ? dimension.height * 0.5
        : 600
      : dimension.height * 0.6;

  return (
    <SociollaCard
      titleStyle={styles.titleStyle}
      style={styles.fullWidth}
      header={<CardHeaderCustom title="Customer Pinpoint" />}
      onPress={() => null}>
      <View style={styles.searchWrapper}>
        <View style={styles.filterModeContainer}>
          <SelectBox
            label="Filter By"
            options={filterModeOptions}
            value={
              filterModeOptions.find((option) => option._id === filterMode) ||
              null
            }
            onChange={(value) => {
              const newMode = value?._id || 'state';
              setFilterMode(newMode);
              if (newMode === 'customer') {
                setSelectedState(null);
                selectedStateRef.current = null;
              } else if (newMode === 'state') {
                setSearchQuery('');
                setSearchResults([]);
                setShowSearchDropdown(false);
                setIsSearchMode(false);
              }
            }}
            trackBy="_id"
            labelKey="name"
            editable={true}
            loading={false}
            withReset={false}
            hideSearch={false}
            selectContainer={{ maxWidth: 200 }}
          />
        </View>
        <View style={styles.filtersRow}>
          {filterMode === 'state' && (
            <View style={styles.stateFilterContainer}>
              <SelectBox
                label={isVn() ? 'City ID' : 'State ID'}
                options={states}
                value={selectedState}
                onChange={handleStateChange}
                trackBy="_id"
                labelKey="name"
                editable={!stateLoading}
                loading={stateLoading}
                withReset={false}
                hideSearch={false}
                selectContainer={{ flex: 1 }}
              />
            </View>
          )}
          {filterMode === 'customer' && (
            <View style={styles.searchInputContainer}>
              <Input
                label=""
                placeholder="Search customer by name or code..."
                value={searchQuery}
                onChangeText={handleSearchChange}
                editable={!searchLoading}
                onFocus={() => {
                  if (searchResults.length > 0) {
                    setShowSearchDropdown(true);
                  }
                }}
                containerStyle={styles.searchInput}
              />
              {searchLoading && (
                <View style={styles.searchLoadingContainer}>
                  <Loader />
                </View>
              )}
              {(isWeb || isMobileWeb) &&
                showSearchDropdown &&
                searchResults.length > 0 && (
                  <View style={styles.dropdownContainer}>
                    <ScrollView
                      style={styles.dropdown}
                      keyboardShouldPersistTaps="handled"
                      nestedScrollEnabled={true}>
                      {searchResults.map((customer, index) => {
                        const customerId = customer._id || '';
                        return (
                          <TouchableOpacity
                            key={`${customerId}_${index}`}
                            style={styles.dropdownItem}
                            delayPressIn={0}
                            onPress={() => {
                              setShowSearchDropdown(false);
                              setTimeout(() => {
                                focusMapToCustomer(customer);
                              }, 50);
                            }}
                            activeOpacity={0.7}>
                            <SociollaText style={styles.dropdownItemName}>
                              {customer.name || 'Unknown Customer'}
                            </SociollaText>
                            {customer.code && (
                              <SociollaText style={styles.dropdownItemCode}>
                                Code: {customer.code}
                              </SociollaText>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  </View>
                )}
            </View>
          )}
          {((filterMode === 'state' && selectedState) ||
            (filterMode === 'customer' && searchQuery.length > 0)) && (
            <TouchableOpacity
              onPress={handleClearSearch}
              style={styles.unifiedResetButton}
              activeOpacity={0.8}>
              <SociollaText style={styles.unifiedResetText}>Reset</SociollaText>
            </TouchableOpacity>
          )}
        </View>
        {!isWeb && !isMobileWeb && (
          <Modal
            visible={showSearchDropdown && searchResults.length > 0}
            transparent={true}
            animationType="slide"
            onRequestClose={() => {
              setSearchResults([]);
              setShowSearchDropdown(false);
              setIsSearchMode(false);
              setSearchQuery('');
            }}>
            <TouchableWithoutFeedback
              onPress={() => {
                setSearchResults([]);
                setShowSearchDropdown(false);
                setIsSearchMode(false);
                setSearchQuery('');
              }}>
              <View style={styles.modalOverlay}>
                <TouchableWithoutFeedback onPress={() => {}}>
                  <View
                    style={[
                      styles.modalContent,
                      {
                        height: Math.min(
                          Math.max(60 + searchResults.length * 70 + 20, 150),
                          dimension.height * 0.7,
                        ),
                      },
                    ]}>
                    <View style={styles.modalHeader}>
                      <SociollaText style={styles.modalTitle}>
                        Search Results
                      </SociollaText>
                      <TouchableOpacity
                        onPress={() => {
                          setSearchResults([]);
                          setShowSearchDropdown(false);
                          setIsSearchMode(false);
                          setSearchQuery('');
                        }}
                        style={styles.modalCloseButton}>
                        <Image
                          width={13}
                          height={13}
                          style={{ width: 13, height: 13 }}
                          svg={true}
                          source={require('@assets/img/icons/close-grey.svg')}
                        />
                      </TouchableOpacity>
                    </View>
                    <ScrollView
                      style={[
                        styles.modalScrollView,
                        {
                          maxHeight: Math.min(
                            searchResults.length * 70,
                            dimension.height * 0.6,
                          ),
                        },
                      ]}
                      contentContainerStyle={styles.modalScrollContent}
                      keyboardShouldPersistTaps="handled"
                      nestedScrollEnabled={true}
                      showsVerticalScrollIndicator={true}>
                      {searchResults.map((customer, index) => {
                        const customerId = customer._id || '';
                        return (
                          <TouchableOpacity
                            key={`${customerId}_${index}`}
                            style={styles.modalItem}
                            delayPressIn={0}
                            onPress={() => {
                              setShowSearchDropdown(false);
                              setTimeout(() => {
                                focusMapToCustomer(customer);
                              }, 50);
                            }}
                            activeOpacity={0.7}>
                            <SociollaText style={styles.modalItemName}>
                              {customer.name || 'Unknown Customer'}
                            </SociollaText>
                            {customer.code && (
                              <SociollaText style={styles.modalItemCode}>
                                Code: {customer.code}
                              </SociollaText>
                            )}
                          </TouchableOpacity>
                        );
                      })}
                    </ScrollView>
                  </View>
                </TouchableWithoutFeedback>
              </View>
            </TouchableWithoutFeedback>
          </Modal>
        )}
      </View>
      {/* {mapLoadError ? (
        <View
          style={[
            styles.mapContainer,
            styles.errorContainer,
            { height: mapHeight },
          ]}>
          <SociollaText style={styles.errorText}>
            Google Maps failed to load
          </SociollaText>
          <SociollaText style={styles.errorSubtext}>
            Please check your internet connection and try again
          </SociollaText>
        </View>
      ) : loading && customers.length === 0 ? (
        <View style={[styles.mapContainer, { height: mapHeight }]}>
          <Loader />
        </View>
      ) : ( */}
      <View style={[styles.mapContainer, { height: mapHeight }]}>
        {isNative ? (
          mapHtml ? (
            <View style={{ position: 'relative', flex: 1 }}>
              <WebViews
                ref={webViewRef}
                originWhitelist={['*']}
                source={{ html: mapHtml, baseUrl: getMapWebViewBaseUrl() }}
                javaScriptEnabled={true}
                domStorageEnabled={true}
                startInLoadingState={true}
                style={{ height: mapHeight, flex: 1 }}
                onMessage={handleWebViewMessage}
                onLoad={() => {}}
                onError={(syntheticEvent) => {
                  const { nativeEvent } = syntheticEvent;
                  console.error('WebView error:', nativeEvent);
                  // setMapLoadError(true);
                }}
                onHttpError={(syntheticEvent) => {
                  const { nativeEvent } = syntheticEvent;
                  console.error('WebView HTTP error:', nativeEvent);
                  if (nativeEvent.statusCode >= 400) {
                    // setMapLoadError(true);
                  }
                }}
              />
              {loading && (
                <View style={styles.loadingOverlay}>
                  <Loader />
                  <SociollaText style={styles.loadingText}>
                    Loading customers...
                  </SociollaText>
                </View>
              )}
            </View>
          ) : (
            <Loader />
          )
        ) : (
          <View
            style={{
              position: 'relative',
              flex: 1,
              width: '100%',
              minHeight: mapHeight,
            }}>
            <div
              id="customer-pinpoint-map"
              style={{
                width: '100%',
                height: `${mapHeight}px`,
                minHeight: `${mapHeight}px`,
                maxHeight: `${mapHeight}px`,
                display: 'block',
                position: 'relative',
                backgroundColor: '#f0f0f0',
                boxSizing: 'border-box',
                overflow: 'hidden',
              }}
            />
            {loading && (
              <View style={styles.loadingOverlay}>
                <Loader />
                <SociollaText style={styles.loadingText}>
                  Loading customers...
                </SociollaText>
              </View>
            )}
          </View>
        )}
      </View>
      {/* )} */}
    </SociollaCard>
  );
};

const styles = StyleSheet.create({
  fullWidth: {
    width: '100%',
  },
  titleStyle: {
    marginBottom: 10,
  },
  mapContainer: {
    width: '100%',
    borderRadius: 4,
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: Colors.grey0,
    marginBottom: 10,
    position: 'relative',
    minHeight:
      isWeb || isMobileWeb
        ? isMobileWeb
          ? dimension.height * 0.5
          : 600
        : dimension.height * 0.6,
  },
  loadingOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backgroundColor: Colors.grey12,
  },
  loadingText: {
    marginTop: 10,
    fontSize: 14,
    color: Colors.grey1,
  },
  searchWrapper: {
    marginBottom: 15,
    position: 'relative',
    zIndex: 1000,
    alignItems: 'flex-start',
  },
  filterModeContainer: {
    width: '100%',
    marginBottom: 12,
    maxWidth: 200,
  },
  filtersRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: 12,
    width: '100%',
    marginBottom: 15,
  },
  stateFilterContainer: {
    flex: 1,
    maxWidth: 300,
  },
  searchInputContainer: {
    position: 'relative',
    flex: 1,
    maxWidth: 500,
  },
  unifiedResetButton: {
    padding: 8,
    justifyContent: 'center',
    alignItems: 'center',
    height: isWeb ? 35 : 45,
    minWidth: 60,
    alignSelf: 'flex-end',
  },
  unifiedResetText: {
    color: Colors.grey1,
    fontSize: 13,
  },
  searchInput: {
    marginBottom: 0,
    flex: 1,
  },
  searchLoadingContainer: {
    justifyContent: 'center',
    alignItems: 'center',
    height: isWeb ? 35 : 45,
  },
  dropdownContainer: {
    position: 'absolute',
    top: '100%',
    left: 0,
    right: 0,
    marginTop: 4,
    backgroundColor: Colors.white,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: Colors.grey0,
    maxHeight: 200,
    zIndex: 1001,
    elevation: 5,
    shadowColor: Colors.black,
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    width: '100%',
  },
  dropdown: {
    maxHeight: 200,
  },
  dropdownItem: {
    padding: 12,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey12,
  },
  dropdownItemName: {
    fontSize: 14,
    color: Colors.grey1,
    fontWeight: '500',
    marginBottom: 4,
  },
  dropdownItemCode: {
    fontSize: 12,
    color: Colors.grey2,
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: Colors.white,
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: dimension.height * 0.7,
    overflow: 'hidden',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey12,
    height: 60,
  },
  modalTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: Colors.grey1,
  },
  modalCloseButton: {
    padding: 4,
    justifyContent: 'center',
    alignItems: 'center',
    width: 32,
    height: 32,
  },
  modalScrollView: {
    flexGrow: 0,
  },
  modalScrollContent: {
    paddingBottom: 20,
  },
  modalItem: {
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: Colors.grey12,
  },
  modalItemName: {
    fontSize: 12,
    color: Colors.grey1,
    fontWeight: '500',
    marginBottom: 4,
  },
  modalItemCode: {
    fontSize: 12,
    color: Colors.grey2,
  },
});

export default CustomerPinpointDashboard;
